import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventsAPI, SITE_COLORS } from '../../lib/api';
import { formatDate, getDayName } from '../../lib/utils';
import { ArrowLeft, Calendar as CalendarIcon, MapPin } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { SEO } from '../seo/SEO';

export const EventsPage = ({ siteKey }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await eventsAPI.getAll(siteKey);
        setEvents(response.data);
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [siteKey]);

  const eventDates = events.map(e => new Date(e.start_date));

  const filteredEvents = events.filter(event => {
    const eventDate = new Date(event.start_date);
    return eventDate.getMonth() === selectedDate.getMonth() && 
           eventDate.getFullYear() === selectedDate.getFullYear();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="Kalendář akcí"
        description={`Přehled nadcházejících akcí, událostí a významných dnů v ${siteKey === 'ms' ? 'MŠ Cestička' : 'dětské skupině Chaloupka na vršku'}. Nenechte si nic ujít.`}
        keywords={['kalendář', 'akce', 'události', 'termíny']}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">Kalendár akcí</h1>
          <p className="text-[#64748B] mt-2">Prehled nadcházejících událostí a akcí</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border-3 border-[#F0F4F8] p-4 sticky top-24">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                modifiers={{ hasEvent: eventDates }}
                modifiersStyles={{
                  hasEvent: { backgroundColor: `${colors.primary}20`, borderRadius: '50%' }
                }}
                className="rounded-md"
                data-testid="events-calendar"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-xl font-semibold text-[#2D3748] mb-6">
              {selectedDate.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' })}
            </h2>
            
            {filteredEvents.length > 0 ? (
              <div className="space-y-4">
                {filteredEvents.map((event, index) => (
                  <div 
                    key={event.id} 
                    className="bg-white rounded-2xl border-3 border-[#F0F4F8] p-6 hover:shadow-lg transition-shadow animate-fade-in-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`event-${index}`}
                  >
                    <div className="flex gap-4">
                      <div 
                        className="event-date-badge flex-shrink-0"
                        style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                      >
                        <span className="text-2xl font-bold">{new Date(event.start_date).getDate()}</span>
                        <span className="text-xs">
                          {new Date(event.start_date).toLocaleDateString('cs-CZ', { month: 'short' })}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-[#2D3748] mb-2">{event.title}</h3>
                        {event.description && (
                          <p className="text-[#64748B] mb-4">{event.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm text-[#64748B]">
                          <div className="flex items-center gap-2">
                            <CalendarIcon size={14} />
                            {getDayName(event.start_date)}, {formatDate(event.start_date)}
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2">
                              <MapPin size={14} />
                              {event.location}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl border-3 border-[#F0F4F8]">
                <CalendarIcon size={48} className="mx-auto text-[#CBD5E1] mb-4" />
                <div className="text-[#64748B]">V tomto mesíci nejsou žádné plánované akce</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;
